import { useState } from "react";
import { BillingSkeleton } from "@/components/SkeletonLoaders";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Calendar, Receipt, ArrowUpRight, AlertCircle, Copy, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSubscription, PLAN_LABELS, PLAN_PRICES } from "@/lib/subscriptionContext";
import { useAuth } from "@/lib/authContext";
import { toast } from "sonner";
import PlanComparisonSheet from "@/components/PlanComparisonSheet";
import { supabase } from "@/integrations/supabase/client";

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, invoices, loading } = useSubscription();
  const [compareOpen, setCompareOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    try {
      setPortalLoading(true);
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { origin: window.location.origin },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url as string;
        return;
      }
      throw new Error("No portal URL returned");
    } catch (err) {
      console.error(err);
      toast.error("Could not open billing portal. Please try again.");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="border-b border-border bg-card">
          <div className="container py-4 flex items-center justify-between">
            <span className="font-heading text-xl font-medium tracking-tight text-foreground">
              oplif<span className="text-primary">.</span>
            </span>
          </div>
        </div>
        <BillingSkeleton />
      </div>
    );
  }

  if (!subscription) {
    navigate("/auth");
    return null;
  }

  const plan = subscription.plan;
  const price = PLAN_PRICES[plan].monthly;
  const renewalDate = subscription.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—";

  const copyReferralCode = () => {
    if (subscription.referral_code) {
      navigator.clipboard.writeText(subscription.referral_code);
      toast.success("Referral code copied!");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center justify-between">
          <span className="font-heading text-xl font-medium tracking-tight text-foreground cursor-pointer" onClick={() => navigate("/")}>
            oplif<span className="text-primary">.</span>
          </span>
          <Button size="sm" variant="outline" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
        </div>
      </div>

      <div className="container pt-8 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl font-medium text-foreground mb-2">Billing & Subscription</h1>
          <p className="text-muted-foreground mb-8">Review your plan, track usage, and manage your billing.</p>
        </motion.div>

        {/* Current Plan Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border shadow-card mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <h2 className="font-heading font-medium text-lg text-foreground">Current Plan</h2>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-heading text-3xl font-medium text-foreground">{PLAN_LABELS[plan]}</span>
                {price > 0 && <span className="text-muted-foreground">${price}/mo</span>}
              </div>
              <span className={`inline-block mt-2 text-xs font-medium px-2 py-1 rounded-full ${
                subscription.status === "active" ? "bg-primary/10 text-primary" :
                subscription.status === "trialing" ? "bg-accent/10 text-accent" :
                "bg-destructive/10 text-destructive"
              }`}>
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </span>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setCompareOpen(true)}>
              {plan === "free" ? "Upgrade" : "Change Plan"} <ArrowUpRight className="w-3 h-3" />
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-sm text-muted-foreground border-t border-border pt-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 shrink-0" />
              <span>Renews {renewalDate}</span>
            </div>
            <div>
              Projects: {subscription.projects_used} used
            </div>
            <div>
              Exports: {subscription.exports_used} used
            </div>
          </div>
        </motion.div>

        {/* Usage bars */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-card rounded-2xl p-6 border border-border shadow-card mb-6"
        >
          <h2 className="font-heading font-medium text-foreground mb-4">Usage This Period</h2>
          {[
            { label: "Projects", used: subscription.projects_used, max: plan === "enterprise" ? 9999 : plan === "business" ? 50 : plan === "pro" ? 10 : 1 },
            { label: "PDF Exports", used: subscription.exports_used, max: plan === "enterprise" ? 9999 : plan === "business" ? 500 : plan === "pro" ? 50 : 2 },
          ].map((u) => (
            <div key={u.label} className="mb-3 last:mb-0">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-foreground font-medium">{u.label}</span>
                <span className="text-muted-foreground">{u.used} / {u.max >= 9999 ? "∞" : u.max}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min((u.used / u.max) * 100, 100)}%` }} />
              </div>
            </div>
          ))}
        </motion.div>

        {/* Referral */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-card rounded-2xl p-6 border border-border shadow-card mb-6"
        >
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-5 h-5 text-primary" />
            <h2 className="font-heading font-medium text-foreground">Refer & Earn</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Share your referral code. When someone you refer upgrades, you both earn a free month.</p>
          <div className="flex items-center gap-2">
            <code className="bg-muted px-3 py-2 rounded-lg text-sm font-mono text-foreground flex-1">{subscription.referral_code || "—"}</code>
            <Button variant="outline" size="sm" onClick={copyReferralCode}><Copy className="w-4 h-4" /></Button>
          </div>
        </motion.div>

        {/* Invoices */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="bg-card rounded-2xl p-6 border border-border shadow-card mb-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Receipt className="w-5 h-5 text-primary" />
            <h2 className="font-heading font-medium text-foreground">Invoices</h2>
          </div>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices yet — they'll appear here after your first payment.</p>
          ) : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-foreground">{inv.description}</div>
                    <div className="text-xs text-muted-foreground">{new Date(inv.invoice_date).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground">${(inv.amount / 100).toFixed(2)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${inv.status === "paid" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                      {inv.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Cancel */}
        {plan !== "free" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-card rounded-2xl p-6 border border-destructive/20 shadow-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              <h2 className="font-heading font-medium text-foreground">Cancel Subscription</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Your access will continue through the end of your current billing period, then revert to the Free plan.</p>
            <Button
              variant="outline"
              className="text-destructive border-destructive/30 hover:bg-destructive/5"
              onClick={openPortal}
              disabled={portalLoading}
            >
              {portalLoading ? "Opening…" : "Manage / Cancel Subscription"}
            </Button>
          </motion.div>
        )}
        <PlanComparisonSheet open={compareOpen} onOpenChange={setCompareOpen} />
      </div>
    </div>
  );
}
